"use client";
import React, { useEffect, useRef, useState } from "react";
import onePageCheckoutMachineCreator from "./OnePageCheckout.machine";
import { useActor, useInterpret } from "@xstate/react";
import AvailabilitySelectionModule from "@/components/molecules/AvailabilitySelectionModule";
import SignupModule from "@/components/molecules/SignupModule/SignupModule.component";
import PaymentModule, {
  PaymentModuleState,
} from "@/components/molecules/PaymentModule/PaymentModule.component";
import LoadingModule from "@/components/molecules/LoadingModule/LoadingModule.component";
import { usePostHog } from "posthog-js/react";
import ErrorModule from "@/components/molecules/ErrorModule/ErrorModule.component";

const calculateSessionCost = (duration: 30 | 60, cost: number) => {
  return duration === 30 ? cost / 2 : cost;
};

const Authenticating = () => {
  return (
    <LoadingModule messages={["Authenticating", "Verifying infomation"]} />
  );
};

const ANALYTICS_PREFIX = "[ONE PAGE CHECKOUT]";

const OnePageCheckoutView = ({
  OnePageCheckoutMachine,
}: {
  OnePageCheckoutMachine: ReturnType<typeof onePageCheckoutMachineCreator>;
}) => {
  const posthog = usePostHog();

  const [last_event, setLastEvent] = useState<string | undefined>();

  const service = useInterpret(
    OnePageCheckoutMachine,
    {
      devTools: true,
    },
    (state) => {
      const getCurrentStatePath = (paths: string[]) => {
        return paths[paths.length - 1];
      };
      const event_name = `${ANALYTICS_PREFIX} ${getCurrentStatePath(
        state.toStrings()
      )}`;

      // De-dup events
      if (last_event !== event_name) {
        posthog.capture(event_name);
        setLastEvent(event_name);
      }

      if (state.matches("Payment.ACCEPT_DETAILS")) {
        // Critical event for analytics
        posthog.capture(`${ANALYTICS_PREFIX} began checkout`);
      }

      // if hits a Payment Error
      if (state.matches("Payment.CHECKOUT_ERROR")) {
        posthog.capture(`${ANALYTICS_PREFIX} checkout_error`, {
          error: state.context.checkout_error,
        });
        // @ts-ignore
        !!window.Intercom && window.Intercom("show");
      }
      // if hits a Signup Error
      if (state.matches("Signup.ERROR")) {
        posthog.capture(`${ANALYTICS_PREFIX} signup_error`, {
          error: state.context.checkout_error,
        });
        // Type-safe check for Intercom
        const w = window as any;
        if (w.Intercom) {
          w.Intercom("show");
        }
      }
    }
  );

  const [state, send] = useActor(service);

  const availability_actor = state.children["availability_selection"];

  const [payment_module_state, setPaymentModuleState] =
    useState<PaymentModuleState>("CREATING_ACCOUNT");

  // Payment Module State
  useEffect(() => {
    if (!state) {
      return;
    }
    switch (true) {
      case state.matches("Payment.CREATING_ACCOUNT"):
        setPaymentModuleState("CREATING_ACCOUNT");
        break;
      case state.matches("Payment.ACCEPT_DETAILS"):
        setPaymentModuleState("ACCEPTING_DETAILS");
        break;
      case state.matches("Payment.CHECKING_OUT"):
        setPaymentModuleState("CHECKING_OUT");
        break;
      case state.matches("Payment.CHECKOUT_ERROR"):
        setPaymentModuleState("ERROR");
        break;
    }
    if (state.matches("Signup.REDIRECT_BOOKING")) {
      // redirect to booking page
      window.location.replace(
        `${process.env.NEXT_PUBLIC_WEBAPP_URL}/profile/${state.context.mentor_info.slug}/book-session?state=from_checkout`
      );
    }
  }, [state]);

  const goToLogin = () => send("LOGIN");

  switch (true) {
    case state.matches("Signup.AUTHENTICATING"):
      return <Authenticating />;
    case state.matches("Signup.ACCEPT_DETAILS"):
      return (
        <SignupModule
          session_info={{
            duration: state.context.duration,
            start_time: state.context.slot,
            // cost: state.context.cost,
            full_name: `${state.context.mentor_info.first_name} ${state.context.mentor_info.last_name}`,
          }}
          changeDuration={() => {
            posthog.capture(`${ANALYTICS_PREFIX} change_booking`);
            send("CHANGE_BOOKING");
          }}
          handleSignup={(values, setSubmitting) => {
            setSubmitting(true);
            try {
              // Critical event for analytics
              posthog.capture(`${ANALYTICS_PREFIX} created account`, {
                $set: { name: values.full_name, email: values.email },
              });
            } catch (e) {
              console.error(e);
            }
            // Split the users full name into given_name and family_name
            const [given_name, ...family_names] = values.full_name.split(" ");
            const family_name =
              family_names.length > 0 ? family_names.join(" ") : "";
            send({
              type: "SIGNUP",
              data: {
                given_name,
                family_name,
                email: values.email,
              },
            });
          }}
          goToLogin={goToLogin}
        />
      );
    case state.matches("Signup.CREATING_USER"):
      return (
        <LoadingModule
          messages={[
            "Authenticating",
            "Creating your profile",
            "Creating your wallet",
            "Finishing up",
          ]}
          iterationSpeed={3800}
        />
      );
    case state.matches("Payment"):
      return (
        <PaymentModule
          session_info={{
            duration: state.context.duration,
            start_datetime: state.context.slot,
            full_name: `${state.context.mentor_info.first_name} ${state.context.mentor_info.last_name}`,
            cost: calculateSessionCost(
              state.context.duration,
              state.context.mentor_info.cost
            ),
          }}
          checkout_error={state.context.checkout_error}
          state={payment_module_state}
          stripe_client_secret={state.context.stripe_client_secret}
          onCheckoutError={(error) => send("CHECKOUT_ERROR", { error })}
          dismissCheckoutError={() => send("DISMISS_ERROR")}
          onStartCheckout={() => {
            posthog.capture(`${ANALYTICS_PREFIX} confirm order`);
            send("CHECKOUT");
          }}
          redirect_url={state.context.redirect_url}
        />
      );
    case ["Payment.CHECKOUT_ERROR", "Signup.ERROR"].some(state.matches):
      <ErrorModule
        title={""}
        description={""}
        dismiss={() => send("DISMISS_ERROR")}
        contactSupport={() => {
          const w = window as any;
          if (w.Intercom) {
            w.Intercom("show");
          }
        }}
      />;
    default:
      return availability_actor ? (
        <AvailabilitySelectionModule
          track={(event_name, meta) =>
            posthog.capture(`${ANALYTICS_PREFIX} ${event_name}`, meta)
          }
          availbility_actor_ref={availability_actor}
        />
      ) : null;
  }
};

export default OnePageCheckoutView;
